import { useCallback } from "react";

import { Action, Marker, MarkerTrapezoidShading, MarkerType, StencilType } from "../../types";
import { clearClassicEndPoint, frameStencilController, getStencilById } from "../../utils";
import { UpdateFrameStencils } from "./use-update-frame-stencils";

/** ================================
 * Get Updated Marker Control Points
 ================================ */
/** helper function to calculate the updated marker object with new point positions */
interface GetUpdatedMarkerControlPointsProps {
  marker: Marker;
  pointIndex: number;
  deltaX: number;
  deltaY: number;
}
const getUpdatedMarkerControlPoints = ({
  marker,
  pointIndex,
  deltaX,
  deltaY,
}: GetUpdatedMarkerControlPointsProps): Marker => {
  const { markerType } = marker;

  switch (markerType) {
    case MarkerType.SHADING_RECTANGLE: {
      const { pos, width, height } = marker;

      switch (pointIndex) {
        case 0:
          return {
            ...marker,
            pos: { x: pos.x + deltaX, y: pos.y + deltaY },
            width: width - deltaX,
            height: height - deltaY,
          };
        case 1:
          return { ...marker, pos: { ...pos, y: pos.y + deltaY }, width: width + deltaX, height: height - deltaY };
        case 2:
          return { ...marker, width: width + deltaX, height: height + deltaY };
        case 3:
          return { ...marker, pos: { ...pos, x: pos.x + deltaX }, width: width - deltaX, height: height + deltaY };
        default:
          return marker;
      }
    }

    case MarkerType.SHADING_OVAL: {
      const { pos, rx, ry } = marker;
      const deltaRX = deltaX / 2;
      const deltaRY = deltaY / 2;
      const newPos = { ...pos, x: pos.x + deltaRX, y: pos.y + deltaRY };
      switch (pointIndex) {
        case 0:
          return { ...marker, pos: newPos, rx: rx - deltaRX, ry: ry - deltaRY };
        case 1:
          return { ...marker, pos: newPos, rx: rx + deltaRX, ry: ry - deltaRY };
        case 2:
          return { ...marker, pos: newPos, rx: rx + deltaRX, ry: ry + deltaRY };
        case 3:
          return { ...marker, pos: newPos, rx: rx - deltaRX, ry: ry + deltaRY };
        default:
          return marker;
      }
    }

    case MarkerType.SHADING_TRAPEZOID: {
      const { pos, shadeVertices } = marker;
      const newPos: MarkerTrapezoidShading["pos"] =
        pointIndex === 0 ? { ...pos, x: pos.x + deltaX, y: pos.y + deltaY } : pos;
      const newShadeVertices: MarkerTrapezoidShading["shadeVertices"] = [
        ...shadeVertices.slice(0, pointIndex),
        { x: (shadeVertices[pointIndex]?.x || 0) + deltaX, y: (shadeVertices[pointIndex]?.y || 0) + deltaY },
        ...shadeVertices.slice(pointIndex + 1),
      ];
      return { ...marker, pos: newPos, shadeVertices: newShadeVertices };
    }

    default:
      return marker;
  }
};

/** ================================
 * Get Updated Action Control Points
 ================================ */
/** helper function to calculate the updated action object with new point position */
interface GetUpdatedActionControlPointsProps {
  action: Action;
  pointIndex: number;
  deltaX: number;
  deltaY: number;
}
const getUpdatedActionControlPoints = ({
  action,
  pointIndex,
  deltaX,
  deltaY,
}: GetUpdatedActionControlPointsProps): Action => {
  const { pos, midPoints, endPos } = action;

  // if pointIndex is 0, it is the action lines start point, so return with new start point position
  if (pointIndex === 0) return { ...action, pos: { x: pos.x + deltaX, y: pos.y + deltaY } };

  // straight action line..
  if (midPoints.length === 0) {
    // if pointIndex is 1, it is the end point, so return with new end point position
    if (pointIndex === 1) return { ...action, endPos: { x: endPos.x + deltaX, y: endPos.y + deltaY } };
    else return action;
  } else {
    const { midPoints } = action;
    // if pointIndex is 1 greate than the midPoints length, then it is the end point so return with new end point pos
    if (pointIndex === midPoints.length + 1)
      return { ...action, endPos: { x: endPos.x + deltaX, y: endPos.y + deltaY } };
    // else, return midPoints with updated point position at the appropriate point index
    else
      return {
        ...action,
        midPoints: midPoints.map((point, i) => {
          if (i + 1 === pointIndex) return { ...point, x: point.x + deltaX, y: point.y + deltaY };
          else return point;
        }),
      };
  }
};

/** ================================
 * Update Stencil Control Points Props
 ================================ */
export interface UpdateStencilControlPointsProps {
  id: string;
  pointIndex: number;
  deltaX: number;
  deltaY: number;
}
export type UpdateStencilControlPointsFunc = (props: UpdateStencilControlPointsProps) => void;

export const useUpdateStencilControlPoints = (
  updateFrameStencils: UpdateFrameStencils
): UpdateStencilControlPointsFunc => {
  return useCallback(
    ({ id, pointIndex, deltaX, deltaY }: UpdateStencilControlPointsProps) => {
      // frame stencils set state
      updateFrameStencils((prevState) => {
        // find the stencil by its id
        const { stencil } = getStencilById({ stencils: prevState || [], id });
        if (!stencil) return prevState;

        const { stencilType } = stencil;
        switch (stencilType) {
          /** ======== Marker ======== */
          case StencilType.MARKER: {
            // get marker's new points
            const updatedMarker = getUpdatedMarkerControlPoints({ marker: stencil, pointIndex, deltaX, deltaY });
            return frameStencilController({ operation: "UPDATE", stencils: prevState, stencil: updatedMarker });
          }

          /** ======== Action ======== */
          case StencilType.ACTION: {
            // get action's new points
            const updatedAction = getUpdatedActionControlPoints({ action: stencil, pointIndex, deltaX, deltaY });
            return frameStencilController({
              operation: "UPDATE",
              stencils: prevState,
              stencil: clearClassicEndPoint(updatedAction),
            });
          }
        }
      });
    },
    [updateFrameStencils]
  );
};
